/**
 * © 2025-2026 Luxembourg Institute of Science and Technology (LIST). All Rights Reserved
 *
 * Reusable card component for displaying inventory resource details
 */

import { Link } from "gatsby"
import { Card, Tag, Typography, Divider, Row, Col } from "antd"
import * as React from "react"
import theme from "../styles/theme"
import {
  InventoryResourceData,
  TopicsField,
  StakeholdersField,
} from "../types/inventoryResource"

const { Text, Paragraph } = Typography

interface InventoryResourceCardProps {
  resource: InventoryResourceData & { path?: string }
}

// Helper function to normalize topics field (handles both array and strapi_json_value formats)
const getTopicsArray = (topics?: TopicsField): string[] => {
  if (!topics) return []
  if (Array.isArray(topics)) return topics
  return topics.strapi_json_value || []
}

// Helper function to normalize stakeholders field (handles both array and strapi_json_value formats)
const getStakeholdersArray = (stakeholders?: StakeholdersField): string[] => {
  if (!stakeholders) return []
  if (Array.isArray(stakeholders)) return stakeholders
  return stakeholders.strapi_json_value || []
}

const InventoryResourceCard: React.FC<InventoryResourceCardProps> = ({
  resource,
}) => {
  const topicsArray = getTopicsArray(resource.topics)
  const stakeholdersArray = getStakeholdersArray(resource.stakeholders)

  return (
    <Card
      title={
        <Link to={resource.path || `/inventory-resource/${resource.slug}`}>
          <Text ellipsis style={{ maxWidth: "75%", color: theme.primaryColor }}>
            {resource.title}
          </Text>{" "}
          {resource.resourceType && (
            <Tag color={theme.primaryColor}>{resource.resourceType}</Tag>
          )}
        </Link>
      }
      style={{ width: "100%" }}
    >
      {resource.description && (
        <Paragraph ellipsis={{ rows: 3 }}>{resource.description}</Paragraph>
      )}

      <Divider style={{ margin: "12px 0" }} />

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          {topicsArray.length > 0 && (
            <div>
              <Text strong>Topics: </Text>
              {topicsArray.map((topic, idx) => (
                <Tag key={`topic-${idx}`} color={theme.primaryColorFade1}>
                  {topic}
                </Tag>
              ))}
            </div>
          )}
        </Col>

        <Col xs={24} md={12}>
          {stakeholdersArray.length > 0 && (
            <div>
              <Text strong>Stakeholders: </Text>
              {stakeholdersArray.map((stakeholder, idx) => (
                <Tag key={`stakeholder-${idx}`} color={theme.primaryColorFade1}>
                  {stakeholder}
                </Tag>
              ))}
            </div>
          )}
        </Col>
      </Row>
    </Card>
  )
}

export default React.memo(InventoryResourceCard)
